import { defineStore } from 'pinia';
import {formatDate} from "@/utils/dateUtils";

export const useMealPlanStore = defineStore('mealPlan', {
  state: () => ({
    orders: [],
  }),

  actions: {
    addOrder(shopId, orderType, mealPlanId, reservationDateTime) {
      const formattedDate = formatDate(reservationDateTime);

      // Check if an existing order matches the given criteria
      let order = this.orders.find(order =>
        order.shopId === shopId &&
        order.orderType === orderType &&
        order.mealPlanId === mealPlanId &&
        order.date === formattedDate
      );

      // If order exists, return it (prevents duplicate orders)
      if (order) {
        return order;
      }

      // Otherwise, create a new order
      const newOrderId = Date.now().toString(); // Unique orderId
      order = {
        orderId: newOrderId,
        shopId,
        orderType,
        mealPlanId: mealPlanId,
        date: formattedDate,
        reservationDateTime: reservationDateTime,
        products: [],
        totalPrice: 0.00,
      };

      this.orders.push(order);
      return order;
    },

    addProductToOrder(orderId, productId, price, quantity, groupId) {
      const order = this.getOrder(orderId);
      if (!order) {
        console.warn(`Order with ID ${orderId} not found.`);
        return;
      }

      // Find product in the order
      const existingProduct = order.products.find(product => product.id === productId);
      if (existingProduct) {
        existingProduct.quantity += quantity;
        existingProduct.price = price;
      } else {
        order.products.push({ id: productId, price, quantity, groupId });
      }

      // Update total price
      order.totalPrice = order.products.reduce((sum, p) => sum + p.price * p.quantity, 0);
    },

    removeProductFromOrder(orderId, productId, quantityToRemove) {
      const order = this.getOrder(orderId);
      if (!order) {
        console.warn(`Order with ID ${orderId} not found.`);
        return;
      }

      // Find the product in the order
      const productIndex = order.products.findIndex(product => product.id === productId);
      if (productIndex === -1) {
        console.warn(`Product with ID ${productId} not found in order.`);
        return;
      }

      const product = order.products[productIndex];
      if (product.quantity > quantityToRemove) {
        product.quantity -= quantityToRemove;
      } else {
        order.products.splice(productIndex, 1);
      }

      // Recalculate total price
      order.totalPrice = order.products.reduce((sum, p) => sum + p.price * p.quantity, 0);

      // If order is empty, remove it
      if (order.products.length === 0) {
        this.orders = this.orders.filter(o => o.orderId !== orderId);
      }
    },

    setPickupNumberForOrder(orderId, pickupNumber) {
      const order = this.getOrder(orderId);
      if (!order) {
        console.warn(`Order with ID ${orderId} not found.`);
        return;
      }
      order.pickupNumber = pickupNumber;
    },

    setStateForOrder(orderId, state) {
      const order = this.getOrder(orderId);
      if (!order) {
        console.warn(`Order with ID ${orderId} not found.`);
        return;
      }
      order.state = state;
    },

    remove(orderId) {
      this.orders = this.orders.filter(o => o.orderId !== orderId);
    },

    reset() {
      this.orders = [];
    },
  },

  getters: {
    getOrder: state => orderId => state.orders.find(o => o.orderId === orderId),
  },

  persist: {
    enabled: true,
    strategies: [
      {
        key: 'mealPlan',
        storage: localStorage,
      },
    ],
  },
});
